<template>
    <app-layout>
        <template #title> User: "{{ user.name }}" </template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Header -->
            <div class="mb-6 flex items-start justify-between">
                <div class="gap-4 flex items-center">
                    <!-- Avatar -->
                    <div
                        class="h-12 w-12 bg-iron-900 rounded-xl flex shrink-0 items-center justify-center"
                    >
                        <span
                            class="font-semibold text-white tracking-wide text-sm"
                            >{{ userInitials }}</span
                        >
                    </div>
                    <div>
                        <h1 class="text-lg font-semibold text-iron-900">
                            {{ user.name }}
                        </h1>
                        <p class="text-sm text-iron-400 font-mono mt-0.5">
                            {{ user.email }}
                        </p>
                    </div>
                </div>

                <div class="gap-2 flex items-center">
                    <button
                        type="button"
                        :disabled="isGeneratingEmail"
                        class="gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center transition-colors disabled:opacity-50"
                        @click="generateOutreachEmail"
                    >
                        <Mail class="h-3 w-3" />
                        {{
                            isGeneratingEmail
                                ? "Generuji..."
                                : "Připravit email"
                        }}
                    </button>
                    <inertia-link
                        :href="
                            route('app.kiosk.api-logs.index', {
                                user_id: user.id,
                            })
                        "
                        class="gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-iron-900 text-white hover:bg-iron-800 inline-flex items-center transition-colors"
                    >
                        <ScrollText class="h-3 w-3" />
                        {{ $t("kiosk.users.view_api_logs") }}
                    </inertia-link>
                    <inertia-link
                        :href="route('app.kiosk.users.index')"
                        class="px-3 py-1.5 text-xs font-medium rounded-md text-iron-600 hover:text-iron-800 hover:bg-iron-100 transition-colors"
                    >
                        <ArrowLeft class="h-3 w-3" />
                    </inertia-link>
                </div>
            </div>

            <!-- User Info Card -->
            <div
                class="bg-white rounded-xl border-iron-200 mb-6 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <div
                        class="gap-x-8 gap-y-3 sm:grid-cols-5 grid grid-cols-2"
                    >
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                ID
                            </p>
                            <p
                                class="text-sm text-iron-700 font-mono tabular-nums"
                            >
                                {{ user.id }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                Plan
                            </p>
                            <span
                                :class="[
                                    'font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]',
                                    user.is_paying_customer
                                        ? 'bg-emerald-50 text-emerald-600'
                                        : 'bg-iron-100 text-iron-500',
                                ]"
                            >
                                {{
                                    user.is_paying_customer ? "Paying" : "Free"
                                }}
                            </span>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                Created
                            </p>
                            <p class="text-sm text-iron-700">
                                {{
                                    user.created_at
                                        ? formatDate(user.created_at)
                                        : "—"
                                }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                Language
                            </p>
                            <p class="text-sm text-iron-700">
                                {{ user.language || "—" }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                Danger zone
                            </p>
                            <button
                                type="button"
                                class="text-xs font-medium text-red-500 hover:text-red-700 transition-colors"
                                @click="openDeleteConfirmation"
                            >
                                Delete user
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Billing Details Card -->
            <div
                v-if="billingAddresses.length > 0"
                class="bg-white rounded-xl border-iron-200 mb-6 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <div class="gap-2 mb-4 flex items-center">
                        <Receipt class="h-4 w-4 text-iron-500" />
                        <h2 class="text-sm font-semibold text-iron-900">
                            Billing details
                        </h2>
                        <span
                            class="font-semibold px-1.5 py-0.5 bg-iron-100 text-iron-500 rounded-full text-[10px] tabular-nums"
                            >{{ billingAddresses.length }}</span
                        >
                    </div>

                    <div class="divide-iron-100 divide-y">
                        <div
                            v-for="ba in billingAddresses"
                            :key="ba.team_id"
                            class="py-3 first:pt-0 last:pb-0"
                        >
                            <div class="gap-2 mb-2 flex items-center">
                                <span
                                    class="text-sm font-medium text-iron-900"
                                    >{{ ba.team_name }}</span
                                >
                                <span
                                    v-if="ba.personal_team"
                                    class="font-semibold px-1.5 py-0.5 bg-iron-100 text-iron-500 rounded-full text-[10px]"
                                    >Personal</span
                                >
                                <a
                                    v-if="
                                        ba.stripe_id && ba.stripe_customer_url
                                    "
                                    :href="ba.stripe_customer_url"
                                    target="_blank"
                                    rel="noopener"
                                    class="gap-1 text-iron-400 font-mono hover:text-iron-700 inline-flex items-center text-[11px] transition-colors"
                                    :title="ba.stripe_id"
                                >
                                    {{ ba.stripe_id }}
                                    <ExternalLink class="h-2.5 w-2.5" />
                                </a>
                                <span
                                    v-else-if="ba.stripe_id"
                                    class="text-iron-400 font-mono text-[11px]"
                                    :title="ba.stripe_id"
                                    >{{ ba.stripe_id }}</span
                                >
                            </div>

                            <div
                                class="gap-x-8 gap-y-2 sm:grid-cols-4 grid grid-cols-2"
                            >
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Company
                                    </p>
                                    <p class="text-sm text-iron-700">
                                        {{ ba.company || "—" }}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Name
                                    </p>
                                    <p class="text-sm text-iron-700">
                                        {{ formatName(ba) }}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Email
                                    </p>
                                    <p
                                        class="text-sm text-iron-700 font-mono break-all"
                                    >
                                        {{ ba.email || "—" }}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Address
                                    </p>
                                    <p class="text-sm text-iron-700">
                                        {{ formatAddress(ba) }}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Country
                                    </p>
                                    <p
                                        class="text-sm text-iron-700 font-mono tabular-nums"
                                    >
                                        {{ ba.country || "—" }}
                                    </p>
                                </div>
                                <div>
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        CRN (IČ)
                                    </p>
                                    <p
                                        class="text-sm text-iron-700 font-mono tabular-nums"
                                    >
                                        {{ ba.crn || "—" }}
                                    </p>
                                </div>
                                <div class="sm:col-span-2">
                                    <p
                                        class="font-medium text-iron-400 tracking-wider mb-0.5 text-[11px] uppercase"
                                    >
                                        Tax ID (VAT)
                                    </p>
                                    <p
                                        class="text-sm text-iron-700 font-mono tabular-nums"
                                    >
                                        {{ ba.tax_id || "—" }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Affiliate Partner Card -->
            <div
                v-if="affiliateStats"
                class="bg-white rounded-xl border-iron-200 mb-6 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <div class="gap-2 mb-4 flex items-center">
                        <Handshake class="h-4 w-4 text-iron-500" />
                        <h2 class="text-sm font-semibold text-iron-900">
                            {{ $t("kiosk.users.affiliate.title") }}
                        </h2>
                        <span
                            class="font-semibold px-2 py-0.5 bg-violet-50 text-violet-600 inline-block rounded-full text-[11px]"
                        >
                            Active
                        </span>
                    </div>

                    <!-- Stats Grid -->
                    <div
                        class="gap-x-8 gap-y-3 sm:grid-cols-4 mb-4 grid grid-cols-2"
                    >
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                {{ $t("kiosk.users.affiliate.clicks") }}
                            </p>
                            <p
                                class="text-sm text-iron-700 font-mono tabular-nums"
                            >
                                {{ affiliateStats.totalClicks }}
                            </p>
                        </div>
                        <div>
                            <p
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                {{ $t("kiosk.users.affiliate.signups") }}
                            </p>
                            <p
                                class="text-sm text-iron-700 font-mono tabular-nums"
                            >
                                {{ affiliateStats.totalSignups }}
                            </p>
                        </div>
                        <div>
                            <div
                                class="font-medium text-iron-400 tracking-wider mb-1 text-[11px] uppercase"
                            >
                                <div class="">
                                    <div>Name: {{ user.name }}</div>
                                    <div>Email: {{ user.email }}</div>
                                    <div>
                                        {{ t("kiosk.users.last_login") }}:
                                        {{
                                            formatLastLogin(user.last_login_at)
                                        }}
                                        <span
                                            v-if="user.last_login_at"
                                            class="text-iron-400 text-sm"
                                        >
                                            ({{
                                                formatLastLoginFull(
                                                    user.last_login_at,
                                                )
                                            }})
                                        </span>
                                    </div>
                                    <div class="mt-4">
                                        <span class="font-semibold"
                                            >Payment Status:
                                        </span>
                                        <span
                                            :class="
                                                affiliateStats.toPayoutUsd > 0
                                                    ? 'text-emerald-600'
                                                    : 'text-iron-500'
                                            "
                                        >
                                            {{
                                                affiliateStats.toPayoutUsd.toLocaleString()
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Affiliate Links -->
                    <div
                        v-if="affiliateStats.affiliateLinks.length > 0"
                        class="border-iron-100 pt-4 mt-4 border-t"
                    >
                        <p
                            class="font-medium text-iron-400 tracking-wider mb-2 text-[11px] uppercase"
                        >
                            {{ $t("kiosk.users.affiliate.links") }}
                        </p>
                        <div class="gap-2 flex flex-col">
                            <div
                                v-for="al in affiliateStats.affiliateLinks"
                                :key="al.uuid"
                                class="gap-4 bg-iron-50/70 rounded-lg px-3 py-2 flex items-center justify-between"
                            >
                                <div class="min-w-0 flex-1">
                                    <div class="gap-2 flex items-center">
                                        <LinkIcon
                                            class="h-3 w-3 text-iron-400 shrink-0"
                                        />
                                        <span
                                            class="text-iron-500 font-mono truncate text-[12px]"
                                            :title="al.link"
                                        >
                                            {{ al.uuid }}
                                        </span>
                                        <span
                                            class="font-semibold px-1.5 py-0.5 bg-violet-50 text-violet-600 shrink-0 rounded-full text-[10px]"
                                        >
                                            {{ al.percentage }}%
                                        </span>
                                    </div>
                                </div>
                                <div
                                    class="gap-4 text-iron-500 flex shrink-0 items-center text-[11px]"
                                >
                                    <span class="tabular-nums"
                                        >{{ al.clicks }}
                                        {{
                                            $t(
                                                "kiosk.users.affiliate.clicks",
                                            ).toLowerCase()
                                        }}</span
                                    >
                                    <span class="tabular-nums"
                                        >{{ al.signups }}
                                        {{
                                            $t(
                                                "kiosk.users.affiliate.signups",
                                            ).toLowerCase()
                                        }}</span
                                    >
                                    <span class="tabular-nums"
                                        >{{ al.purchases }}
                                        {{
                                            $t(
                                                "kiosk.users.affiliate.purchases",
                                            ).toLowerCase()
                                        }}</span
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs: Event Types / Reservations / Integrations -->
            <Tabs
                default-value="event-types"
                @update:model-value="(v: string) => (activeTab = v)"
            >
                <TabsList
                    class="border-iron-200 gap-0 p-0 h-auto w-full justify-start rounded-none border-b bg-transparent"
                >
                    <TabsTrigger
                        value="event-types"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <Calendar class="h-3.5 w-3.5 mr-1.5" />
                        Event Types
                        <span
                            class="ml-1.5 font-semibold px-1.5 py-0.5 rounded-md text-[11px] tabular-nums"
                            :class="[
                                activeTab === 'event-types'
                                    ? 'bg-iron-900 text-white'
                                    : 'bg-iron-100 text-iron-400',
                            ]"
                            >{{ eventTypes.length }}</span
                        >
                    </TabsTrigger>
                    <TabsTrigger
                        value="reservations"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <TicketCheck class="h-3.5 w-3.5 mr-1.5" />
                        Reservations
                        <span
                            class="ml-1.5 font-semibold px-1.5 py-0.5 rounded-md text-[11px] tabular-nums"
                            :class="[
                                activeTab === 'reservations'
                                    ? 'bg-iron-900 text-white'
                                    : 'bg-iron-100 text-iron-400',
                            ]"
                            >{{ reservations.total }}</span
                        >
                    </TabsTrigger>
                    <TabsTrigger
                        value="integrations"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <Plug class="h-3.5 w-3.5 mr-1.5" />
                        Integrations
                        <span
                            class="ml-1.5 font-semibold px-1.5 py-0.5 rounded-md text-[11px] tabular-nums"
                            :class="[
                                activeTab === 'integrations'
                                    ? 'bg-iron-900 text-white'
                                    : 'bg-iron-100 text-iron-400',
                            ]"
                            >{{ integrations.length }}</span
                        >
                    </TabsTrigger>
                    <TabsTrigger
                        value="tasks"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <ListChecks class="h-3.5 w-3.5 mr-1.5" />
                        Tasks
                    </TabsTrigger>
                    <TabsTrigger
                        value="emails"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <Mail class="h-3.5 w-3.5 mr-1.5" />
                        Emails
                    </TabsTrigger>
                    <TabsTrigger
                        value="invoices"
                        class="px-4 pb-2.5 pt-2 font-medium text-iron-400 data-[state=active]:border-iron-900 data-[state=active]:text-iron-900 hover:text-iron-600 ml-0 rounded-none border-b-2 border-transparent text-[13px] shadow-none ring-0 ring-offset-0 transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0"
                    >
                        <FileText class="h-3.5 w-3.5 mr-1.5" />
                        Invoices
                    </TabsTrigger>
                </TabsList>

                <!-- Event Types Tab -->
                <TabsContent value="event-types" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <!-- Empty -->
                        <div
                            v-if="eventTypes.length === 0"
                            class="py-16 text-center"
                        >
                            <div
                                class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                            >
                                <Calendar class="h-4 w-4 text-iron-400" />
                            </div>
                            <p class="text-sm text-iron-500">
                                No event types found.
                            </p>
                        </div>

                        <!-- Table -->
                        <div v-else class="overflow-x-auto">
                            <table class="w-full">
                                <thead>
                                    <tr class="border-iron-100 border-b">
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                        >
                                            Name
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                        >
                                            Team
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                        >
                                            Slug
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                        >
                                            Duration
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                        >
                                            Status
                                        </th>
                                        <th
                                            class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                        ></th>
                                    </tr>
                                </thead>
                                <tbody class="divide-iron-50 divide-y">
                                    <tr
                                        v-for="et in eventTypes"
                                        :key="et.id"
                                        class="hover:bg-iron-50/70 transition-colors"
                                    >
                                        <td
                                            class="px-5 py-2.5 text-sm font-medium text-iron-900 whitespace-nowrap"
                                        >
                                            {{ et.name }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-sm text-iron-500 whitespace-nowrap"
                                        >
                                            {{ et.team_name ?? "—" }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-iron-400 font-mono text-[13px] whitespace-nowrap"
                                        >
                                            {{ et.slug ?? "—" }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-sm text-iron-500 text-right whitespace-nowrap tabular-nums"
                                        >
                                            {{
                                                et.duration
                                                    ? `${et.duration} min`
                                                    : "—"
                                            }}
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-center whitespace-nowrap"
                                        >
                                            <span
                                                :class="
                                                    et.enabled
                                                        ? 'bg-emerald-50 text-emerald-600'
                                                        : 'bg-iron-100 text-iron-500'
                                                "
                                                class="font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]"
                                            >
                                                {{
                                                    et.enabled
                                                        ? "Active"
                                                        : "Disabled"
                                                }}
                                            </span>
                                        </td>
                                        <td
                                            class="px-5 py-2.5 text-right whitespace-nowrap"
                                        >
                                            <div
                                                class="gap-3 inline-flex items-center"
                                            >
                                                <inertia-link
                                                    :href="
                                                        route(
                                                            'app.kiosk.event-types.slot-debug',
                                                            {
                                                                eventType:
                                                                    et.id,
                                                                user: user.id,
                                                            },
                                                        )
                                                    "
                                                    class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                                    title="Debug slots"
                                                >
                                                    <CalendarSearch
                                                        class="h-3 w-3"
                                                    />
                                                    Debug slots
                                                </inertia-link>
                                                <inertia-link
                                                    v-for="token in et.tokens"
                                                    :key="token"
                                                    :href="
                                                        route(
                                                            'app.kiosk.api-logs.index',
                                                            { token: token },
                                                        )
                                                    "
                                                    class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                                    :title="token"
                                                >
                                                    <ScrollText
                                                        class="h-3 w-3"
                                                    />
                                                    Logs
                                                </inertia-link>
                                                <span
                                                    v-if="
                                                        et.tokens.length === 0
                                                    "
                                                    class="text-xs text-iron-300"
                                                    >—</span
                                                >
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>

                <!-- Reservations Tab -->
                <TabsContent value="reservations" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <!-- Empty -->
                        <div
                            v-if="reservations.data.length === 0"
                            class="py-16 text-center"
                        >
                            <div
                                class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                            >
                                <TicketCheck class="h-4 w-4 text-iron-400" />
                            </div>
                            <p class="text-sm text-iron-500">
                                No reservations found.
                            </p>
                        </div>

                        <!-- Table -->
                        <template v-else>
                            <div class="overflow-x-auto">
                                <table class="w-full">
                                    <thead>
                                        <tr class="border-iron-100 border-b">
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Booker
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Event Type
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Team
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Date
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                            >
                                                Status
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                            ></th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-iron-50 divide-y">
                                        <tr
                                            v-for="r in reservations.data"
                                            :key="r.id"
                                            class="hover:bg-iron-50/70 transition-colors"
                                        >
                                            <td class="px-5 py-2.5">
                                                <div
                                                    class="text-sm font-medium text-iron-900"
                                                >
                                                    {{ r.name ?? "—" }}
                                                </div>
                                                <div
                                                    class="text-iron-400 font-mono mt-0.5 text-[11px]"
                                                >
                                                    {{ r.email }}
                                                </div>
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-sm text-iron-500 whitespace-nowrap"
                                            >
                                                {{ r.event_type_name ?? "—" }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-sm text-iron-500 whitespace-nowrap"
                                            >
                                                {{ r.team_name ?? "—" }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-iron-500 font-mono text-[13px] whitespace-nowrap tabular-nums"
                                            >
                                                {{
                                                    r.start
                                                        ? formatDate(r.start)
                                                        : "—"
                                                }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-center whitespace-nowrap"
                                            >
                                                <span
                                                    :class="
                                                        statusClass(r.status)
                                                    "
                                                    class="font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]"
                                                >
                                                    {{ r.status }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-right whitespace-nowrap"
                                            >
                                                <inertia-link
                                                    :href="
                                                        route(
                                                            'app.kiosk.api-logs.index',
                                                            {
                                                                message_search:
                                                                    r.uuid,
                                                                ...reservationDateRange(
                                                                    r,
                                                                ),
                                                            },
                                                        )
                                                    "
                                                    class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                                    :title="r.uuid"
                                                >
                                                    <ScrollText
                                                        class="h-3 w-3"
                                                    />
                                                    Logs
                                                </inertia-link>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Pagination -->
                            <div
                                v-if="reservations.last_page > 1"
                                class="border-iron-100 px-5 py-3 flex items-center justify-between border-t"
                            >
                                <span
                                    class="text-iron-400 text-[11px] tabular-nums"
                                >
                                    Page
                                    {{ reservations.current_page }} of
                                    {{ reservations.last_page }}
                                    <span class="text-iron-300 ml-1"
                                        >({{ reservations.total }} total)</span
                                    >
                                </span>
                                <div class="gap-1.5 flex">
                                    <inertia-link
                                        v-if="reservations.current_page > 1"
                                        :href="
                                            route('app.kiosk.users.show', {
                                                user: user.id,
                                                page:
                                                    reservations.current_page -
                                                    1,
                                            })
                                        "
                                        class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                                        preserve-scroll
                                    >
                                        Previous
                                    </inertia-link>
                                    <inertia-link
                                        v-if="
                                            reservations.current_page <
                                            reservations.last_page
                                        "
                                        :href="
                                            route('app.kiosk.users.show', {
                                                user: user.id,
                                                page:
                                                    reservations.current_page +
                                                    1,
                                            })
                                        "
                                        class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                                        preserve-scroll
                                    >
                                        Next
                                    </inertia-link>
                                </div>
                            </div>
                        </template>
                    </div>
                </TabsContent>

                <!-- Integrations Tab -->
                <TabsContent value="integrations" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <!-- Empty -->
                        <div
                            v-if="integrations.length === 0"
                            class="py-16 text-center"
                        >
                            <div
                                class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                            >
                                <Plug class="h-4 w-4 text-iron-400" />
                            </div>
                            <p class="text-sm text-iron-500">
                                No integrations found.
                            </p>
                        </div>

                        <!-- Integration List -->
                        <div v-else class="divide-iron-100 divide-y">
                            <div
                                v-for="(
                                    integration, integrationIdx
                                ) in integrations"
                                :key="integrationIdx"
                                class="px-5 py-4"
                            >
                                <div
                                    v-for="(
                                        integrationValue, fieldKey
                                    ) in integration"
                                    :key="fieldKey"
                                >
                                    <!-- Nested data (JSON) -->
                                    <div v-if="String(fieldKey) === 'data'">
                                        <div
                                            v-for="(
                                                dataValue, dataKey
                                            ) in JSON.parse(integrationValue)"
                                            :key="dataKey"
                                            class="gap-4 py-1.5 flex items-baseline"
                                        >
                                            <span
                                                class="font-medium text-iron-400 tracking-wider w-28 shrink-0 text-[11px] uppercase"
                                            >
                                                {{ String(dataKey) }}
                                            </span>
                                            <span
                                                class="min-w-0 text-sm text-iron-700 font-mono break-all"
                                            >
                                                {{ dataValue }}
                                            </span>
                                        </div>
                                    </div>
                                    <!-- Regular field -->
                                    <div
                                        v-else
                                        class="gap-4 py-1.5 flex items-baseline"
                                    >
                                        <span
                                            class="font-medium text-iron-400 tracking-wider w-28 shrink-0 text-[11px] uppercase"
                                        >
                                            {{ String(fieldKey) }}
                                        </span>
                                        <span
                                            class="min-w-0 text-sm text-iron-700 font-mono break-all"
                                        >
                                            {{ integrationValue }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <!-- Tasks Tab (Linear) -->
                <TabsContent value="tasks" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <Deferred data="linearTasks">
                            <template #fallback>
                                <div class="px-5 py-4 space-y-3">
                                    <div
                                        v-for="i in 3"
                                        :key="i"
                                        class="h-12 bg-iron-100 rounded-lg animate-pulse"
                                    ></div>
                                </div>
                            </template>

                            <!-- Empty -->
                            <div
                                v-if="!linearTasks || linearTasks.length === 0"
                                class="py-16 text-center"
                            >
                                <div
                                    class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                                >
                                    <ListChecks class="h-4 w-4 text-iron-400" />
                                </div>
                                <p class="text-sm text-iron-500">
                                    No Linear tasks found.
                                </p>
                            </div>

                            <!-- Task List -->
                            <div v-else class="divide-iron-100 divide-y">
                                <div
                                    v-for="task in linearTasks"
                                    :key="task.id"
                                    class="px-5 py-3"
                                >
                                    <div
                                        class="gap-3 flex items-start justify-between"
                                    >
                                        <div class="min-w-0 flex-1">
                                            <div
                                                class="gap-2 flex items-center"
                                            >
                                                <span
                                                    class="text-iron-400 font-mono shrink-0 text-[11px]"
                                                    >{{ task.identifier }}</span
                                                >
                                                <a
                                                    :href="task.url"
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="text-sm font-medium text-iron-900 hover:text-iron-600 truncate transition-colors"
                                                >
                                                    {{ task.title }}
                                                </a>
                                                <span
                                                    :class="
                                                        linearStateClass(
                                                            task.state_type,
                                                        )
                                                    "
                                                    class="font-semibold px-2 py-0.5 shrink-0 rounded-full text-[11px]"
                                                >
                                                    {{ task.state }}
                                                </span>
                                            </div>
                                            <div
                                                class="gap-4 mt-1 text-iron-400 text-[11px]"
                                            >
                                                <span v-if="task.assignee">{{
                                                    task.assignee
                                                }}</span>
                                                <span
                                                    v-if="task.created_at"
                                                    class="font-mono tabular-nums"
                                                    >{{
                                                        formatDate(
                                                            task.created_at,
                                                        )
                                                    }}</span
                                                >
                                            </div>

                                            <!-- Sub-issues -->
                                            <div
                                                v-if="
                                                    task.children &&
                                                    task.children.length > 0
                                                "
                                                class="mt-2 ml-4 gap-1.5 flex flex-col"
                                            >
                                                <div
                                                    v-for="child in task.children"
                                                    :key="child.id"
                                                    class="gap-2 flex items-center"
                                                >
                                                    <span
                                                        class="text-iron-300 font-mono text-[10px]"
                                                        >{{
                                                            child.identifier
                                                        }}</span
                                                    >
                                                    <span
                                                        class="text-iron-600 truncate text-[12px]"
                                                        >{{ child.title }}</span
                                                    >
                                                    <span
                                                        :class="
                                                            linearStateClass(
                                                                child.state,
                                                            )
                                                        "
                                                        class="font-semibold px-1.5 py-0.5 shrink-0 rounded-full text-[10px]"
                                                    >
                                                        {{ child.state }}
                                                    </span>
                                                    <span
                                                        v-if="child.due_date"
                                                        class="text-iron-400 font-mono text-[10px] tabular-nums"
                                                    >
                                                        due
                                                        {{ child.due_date }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Deferred>
                    </div>
                </TabsContent>

                <!-- Emails Tab -->
                <TabsContent value="emails" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <!-- Empty -->
                        <div
                            v-if="emails.length === 0"
                            class="py-16 text-center"
                        >
                            <div
                                class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                            >
                                <Mail class="h-4 w-4 text-iron-400" />
                            </div>
                            <p class="text-sm text-iron-500">
                                No emails found.
                            </p>
                        </div>

                        <!-- Email Threads -->
                        <div v-else class="divide-iron-100 divide-y">
                            <div
                                v-for="(thread, threadIdx) in emailThreads"
                                :key="threadIdx"
                            >
                                <!-- Thread Header -->
                                <button
                                    type="button"
                                    class="hover:bg-iron-50/70 px-5 py-3 w-full text-left transition-colors"
                                    @click="toggleThread(threadIdx)"
                                >
                                    <div class="gap-3 flex items-center">
                                        <ChevronDown
                                            class="h-3.5 w-3.5 text-iron-400 shrink-0 transition-transform"
                                            :class="{
                                                '-rotate-90':
                                                    !expandedThreads.has(
                                                        threadIdx,
                                                    ),
                                            }"
                                        />
                                        <div class="min-w-0 flex-1">
                                            <div
                                                class="gap-2 flex items-center"
                                            >
                                                <span
                                                    class="text-sm font-medium text-iron-900 truncate"
                                                    >{{ thread.subject }}</span
                                                >
                                                <span
                                                    class="font-semibold px-1.5 py-0.5 bg-iron-100 text-iron-500 shrink-0 rounded-full text-[10px] tabular-nums"
                                                    >{{
                                                        thread.messages.length
                                                    }}</span
                                                >
                                            </div>
                                            <div
                                                class="gap-2 mt-0.5 flex items-center"
                                            >
                                                <span
                                                    class="text-iron-400 font-mono text-[11px] tabular-nums"
                                                    >{{
                                                        formatDate(
                                                            thread.latestDate,
                                                        )
                                                    }}</span
                                                >
                                                <span
                                                    class="text-iron-300 text-[11px]"
                                                    >&middot;</span
                                                >
                                                <span
                                                    class="text-iron-400 truncate text-[11px]"
                                                >
                                                    {{
                                                        thread.messages[
                                                            thread.messages
                                                                .length - 1
                                                        ].body_preview ||
                                                        "No content"
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>

                                <!-- Thread Messages -->
                                <div
                                    v-if="expandedThreads.has(threadIdx)"
                                    class="bg-iron-50/50 border-iron-100 border-t"
                                >
                                    <div
                                        v-for="(
                                            email, emailIdx
                                        ) in thread.messages"
                                        :key="emailIdx"
                                        class="hover:bg-iron-100/50 border-iron-100/50 px-5 py-2.5 cursor-pointer transition-colors"
                                        :class="{
                                            'border-t': emailIdx > 0,
                                        }"
                                        @click="openEmailDetail(email)"
                                    >
                                        <div
                                            class="gap-3 ml-5 flex items-start"
                                        >
                                            <div class="pt-0.5 shrink-0">
                                                <ArrowUpRight
                                                    v-if="
                                                        email.direction ===
                                                        'sent'
                                                    "
                                                    class="h-3.5 w-3.5 text-blue-500"
                                                    :title="'Sent'"
                                                />
                                                <ArrowDownLeft
                                                    v-else
                                                    class="h-3.5 w-3.5 text-emerald-500"
                                                    :title="'Received'"
                                                />
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <div
                                                    class="gap-2 flex items-center"
                                                >
                                                    <span
                                                        class="text-sm font-medium text-iron-700"
                                                        >{{
                                                            email.from_name ||
                                                            email.from
                                                        }}</span
                                                    >
                                                    <span
                                                        class="text-iron-400 font-mono text-[11px] tabular-nums"
                                                        >{{
                                                            email.sent_at
                                                                ? formatDate(
                                                                      email.sent_at,
                                                                  )
                                                                : "—"
                                                        }}</span
                                                    >
                                                </div>
                                                <p
                                                    class="text-iron-500 mt-0.5 line-clamp-2 text-[13px]"
                                                >
                                                    {{
                                                        email.body_preview ||
                                                        "No content"
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <!-- Invoices Tab -->
                <TabsContent value="invoices" class="mt-0">
                    <div
                        class="bg-white rounded-b-xl border-iron-200 overflow-hidden border border-t-0"
                    >
                        <Deferred data="invoices">
                            <template #fallback>
                                <div class="px-5 py-4 space-y-3">
                                    <div
                                        v-for="i in 3"
                                        :key="i"
                                        class="h-12 bg-iron-100 rounded-lg animate-pulse"
                                    ></div>
                                </div>
                            </template>

                            <!-- Empty -->
                            <div
                                v-if="!invoices || invoices.length === 0"
                                class="py-16 text-center"
                            >
                                <div
                                    class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                                >
                                    <FileText class="h-4 w-4 text-iron-400" />
                                </div>
                                <p class="text-sm text-iron-500">
                                    No invoices found.
                                </p>
                            </div>

                            <!-- Invoices Table -->
                            <div v-else class="overflow-x-auto">
                                <table class="w-full">
                                    <thead>
                                        <tr class="border-iron-100 border-b">
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Number
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Team
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                            >
                                                Issued
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                            >
                                                Amount
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                            >
                                                Status
                                            </th>
                                            <th
                                                class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                            ></th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-iron-50 divide-y">
                                        <tr
                                            v-for="invoice in invoices"
                                            :key="invoice.id"
                                            class="hover:bg-iron-50/70 transition-colors"
                                        >
                                            <td
                                                class="px-5 py-2.5 text-sm font-medium text-iron-900 font-mono whitespace-nowrap"
                                            >
                                                <a
                                                    v-if="
                                                        invoice.stripe_invoice_url
                                                    "
                                                    :href="
                                                        invoice.stripe_invoice_url
                                                    "
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="gap-1 hover:text-iron-600 inline-flex items-center transition-colors"
                                                >
                                                    {{
                                                        invoice.internal_id ||
                                                        "—"
                                                    }}
                                                    <ExternalLink
                                                        class="h-3 w-3"
                                                    />
                                                </a>
                                                <span v-else>{{
                                                    invoice.internal_id || "—"
                                                }}</span>
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-sm text-iron-500 whitespace-nowrap"
                                            >
                                                {{ invoice.team_name }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-iron-500 font-mono text-[13px] whitespace-nowrap tabular-nums"
                                            >
                                                {{ invoice.issued_at }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-sm text-iron-700 text-right whitespace-nowrap tabular-nums"
                                            >
                                                {{ invoice.amount_currency }}
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-center whitespace-nowrap"
                                            >
                                                <span
                                                    :class="
                                                        invoiceStatusClass(
                                                            invoice.status,
                                                        )
                                                    "
                                                    class="font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]"
                                                >
                                                    {{ invoice.status }}
                                                </span>
                                            </td>
                                            <td
                                                class="px-5 py-2.5 text-right whitespace-nowrap"
                                            >
                                                <a
                                                    :href="
                                                        route(
                                                            'app.invoices.downloads.pdf',
                                                            {
                                                                invoice:
                                                                    invoice.id,
                                                            },
                                                        )
                                                    "
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="gap-1 text-xs font-medium text-iron-500 hover:text-iron-900 inline-flex items-center transition-colors"
                                                >
                                                    <CloudDownload
                                                        class="h-3 w-3"
                                                    />
                                                    Download
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </Deferred>
                    </div>
                </TabsContent>
            </Tabs>
        </div>

        <!-- Outreach Email Modal -->
        <jet-dialog-modal
            :show="showOutreachModal"
            @close="showOutreachModal = false"
        >
            <template #title> Odeslat email </template>

            <template #content>
                <div class="gap-4 flex flex-col">
                    <div>
                        <label
                            class="font-medium text-iron-700 mb-1 text-sm block"
                            >Od</label
                        >
                        <select
                            v-model="outreachForm.account_email"
                            class="border-iron-300 focus:border-iron-500 focus:ring-iron-500 rounded-md text-sm shadow-sm w-full"
                        >
                            <option
                                v-for="account in outreachAccounts"
                                :key="account.email"
                                :value="account.email"
                            >
                                {{
                                    account.display_name
                                        ? `${account.display_name} <${account.email}>`
                                        : account.email
                                }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label
                            class="font-medium text-iron-700 mb-1 text-sm block"
                            >Komu</label
                        >
                        <input
                            :value="outreachForm.to_email"
                            type="text"
                            readonly
                            class="border-iron-200 bg-iron-50 text-iron-500 rounded-md text-sm w-full"
                        />
                    </div>
                    <div>
                        <label
                            class="font-medium text-iron-700 mb-1 text-sm block"
                            >Předmět</label
                        >
                        <input
                            v-model="outreachForm.subject"
                            type="text"
                            class="border-iron-300 focus:border-iron-500 focus:ring-iron-500 rounded-md text-sm shadow-sm w-full"
                        />
                    </div>
                    <div>
                        <label
                            class="font-medium text-iron-700 mb-1 text-sm block"
                            >Text emailu</label
                        >
                        <textarea
                            v-model="outreachForm.body"
                            rows="12"
                            class="border-iron-300 focus:border-iron-500 focus:ring-iron-500 rounded-md text-sm shadow-sm font-mono leading-relaxed w-full"
                        ></textarea>
                    </div>
                </div>
            </template>

            <template #footer>
                <jet-secondary-button @click="showOutreachModal = false">
                    Zrušit
                </jet-secondary-button>

                <button
                    type="button"
                    class="gap-1.5 ml-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center transition-colors disabled:opacity-50"
                    :disabled="isSendingEmail"
                    @click="sendOutreachEmail"
                >
                    <Send class="h-3.5 w-3.5" />
                    {{ isSendingEmail ? "Odesílám..." : "Odeslat" }}
                </button>
            </template>
        </jet-dialog-modal>

        <!-- First Confirmation Modal -->
        <jet-dialog-modal :show="showFirstConfirmation" @close="cancelDelete">
            <template #title>Delete User</template>

            <template #content>
                <p>
                    Are you sure you want to delete the user
                    <strong>{{ user.name }}</strong> ({{ user.email }})?
                </p>
                <p class="mt-2 text-sm text-iron-600">
                    This will permanently delete the user and all their data
                    including teams, event types, reservations, and
                    integrations.
                </p>
            </template>

            <template #footer>
                <jet-secondary-button @click="cancelDelete">
                    Cancel
                </jet-secondary-button>

                <jet-danger-button
                    class="ml-2"
                    @click="proceedToSecondConfirmation"
                >
                    Yes, Delete User
                </jet-danger-button>
            </template>
        </jet-dialog-modal>

        <!-- Email Detail Modal -->
        <jet-dialog-modal
            :show="selectedEmail !== null"
            @close="selectedEmail = null"
        >
            <template #title>
                {{ selectedEmail?.subject || "No subject" }}
            </template>

            <template #content>
                <div class="gap-y-3 grid">
                    <div class="gap-4 flex items-baseline">
                        <span
                            class="font-medium text-iron-400 tracking-wider w-16 shrink-0 text-[11px] uppercase"
                            >Type</span
                        >
                        <span class="gap-1.5 flex items-center">
                            <ArrowUpRight
                                v-if="selectedEmail?.direction === 'sent'"
                                class="h-3.5 w-3.5 text-blue-500"
                            />
                            <ArrowDownLeft
                                v-else
                                class="h-3.5 w-3.5 text-emerald-500"
                            />
                            <span
                                :class="
                                    selectedEmail?.direction === 'sent'
                                        ? 'bg-blue-50 text-blue-600'
                                        : 'bg-emerald-50 text-emerald-600'
                                "
                                class="font-semibold px-2 py-0.5 rounded-full text-[11px]"
                            >
                                {{
                                    selectedEmail?.direction === "sent"
                                        ? "Sent"
                                        : "Received"
                                }}
                            </span>
                        </span>
                    </div>
                    <div class="gap-4 flex items-baseline">
                        <span
                            class="font-medium text-iron-400 tracking-wider w-16 shrink-0 text-[11px] uppercase"
                            >From</span
                        >
                        <span class="text-sm text-iron-700">
                            {{
                                selectedEmail?.from_name || selectedEmail?.from
                            }}
                            <span
                                v-if="selectedEmail?.from_name"
                                class="text-iron-400 ml-1 text-[12px]"
                                >&lt;{{ selectedEmail?.from }}&gt;</span
                            >
                        </span>
                    </div>
                    <div class="gap-4 flex items-baseline">
                        <span
                            class="font-medium text-iron-400 tracking-wider w-16 shrink-0 text-[11px] uppercase"
                            >Date</span
                        >
                        <span
                            class="text-sm text-iron-700 font-mono tabular-nums"
                        >
                            {{
                                selectedEmail?.sent_at
                                    ? formatDate(selectedEmail.sent_at)
                                    : "—"
                            }}
                        </span>
                    </div>
                    <div class="border-iron-100 mt-2 pt-3 border-t">
                        <p
                            class="text-sm text-iron-700 leading-relaxed whitespace-pre-wrap"
                        >
                            {{ selectedEmail?.body_preview || "No content" }}
                        </p>
                    </div>
                </div>
            </template>

            <template #footer>
                <jet-secondary-button @click="selectedEmail = null">
                    Close
                </jet-secondary-button>
            </template>
        </jet-dialog-modal>

        <!-- Second Confirmation Modal -->
        <jet-dialog-modal :show="showSecondConfirmation" @close="cancelDelete">
            <template #title>Final Confirmation</template>

            <template #content>
                <p class="text-red-600 font-semibold">
                    This action cannot be undone!
                </p>
                <p class="mt-2">
                    You are about to permanently delete
                    <strong>{{ user.name }}</strong> ({{ user.email }}).
                </p>
                <p class="mt-2 text-sm text-iron-600">
                    Click "Permanently Delete" to confirm this action.
                </p>
            </template>

            <template #footer>
                <jet-secondary-button @click="cancelDelete">
                    Cancel
                </jet-secondary-button>

                <jet-danger-button
                    class="ml-2"
                    :class="{ 'opacity-25': deleteForm.processing }"
                    :disabled="deleteForm.processing"
                    @click="confirmDelete"
                >
                    Permanently Delete
                </jet-danger-button>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script lang="ts" setup>
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import {
    Tabs,
    TabsTrigger,
    TabsList,
    TabsContent,
} from "@/components/ui/tabs-v2";
import route from "ziggy-js";
import { Deferred, useForm } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import { useLastLoginFormat } from "@/composables/useLastLoginFormat";
import { useI18n } from "vue-i18n";
import JetDialogModal from "../../../../Jetstream/DialogModal.vue";
import JetDangerButton from "../../../../Jetstream/DangerButton.vue";
import JetSecondaryButton from "../../../../Jetstream/SecondaryButton.vue";
import axios from "axios";
import {
    ScrollText,
    ArrowLeft,
    Calendar,
    CalendarSearch,
    TicketCheck,
    Plug,
    Handshake,
    Link as LinkIcon,
    ListChecks,
    Mail,
    Send,
    ArrowUpRight,
    ArrowDownLeft,
    ChevronDown,
    FileText,
    CloudDownload,
    Receipt,
    ExternalLink,
} from "lucide-vue-next";

type EventTypeItem = {
    id: number;
    name: string;
    slug: string | null;
    duration: number | null;
    enabled: boolean;
    team_name: string | null;
    tokens: string[];
};

type ReservationItem = {
    id: number;
    uuid: string;
    email: string;
    name: string | null;
    status: string;
    start: string | null;
    created_at: string;
    updated_at: string;
    event_type_name: string | null;
    team_name: string | null;
    token: string | null;
};

type PaginatedLink = {
    url: string | null;
    label: string;
    active: boolean;
};

type Paginated<T> = {
    data: T[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginatedLink[];
};

type AffiliateLinkItem = {
    uuid: string;
    percentage: number;
    link: string;
    clicks: number;
    signups: number;
    purchases: number;
};

type LinearChild = {
    id: string;
    identifier: string;
    title: string;
    state: string;
    due_date: string | null;
};

type LinearTask = {
    id: string;
    identifier: string;
    title: string;
    state: string;
    state_type: string;
    assignee: string | null;
    created_at: string;
    due_date: string | null;
    url: string;
    children: LinearChild[];
};

type EmailItem = {
    from: string;
    from_name: string;
    subject: string;
    body_preview: string;
    sent_at: string;
    direction: string;
};

type BillingAddressItem = {
    team_id: number;
    team_name: string;
    personal_team: boolean;
    stripe_id: string | null;
    stripe_customer_url: string | null;
    email: string | null;
    company: string | null;
    first_name: string | null;
    last_name: string | null;
    street: string | null;
    city: string | null;
    postal_code: string | null;
    country: string | null;
    crn: string | null;
    tax_id: string | null;
};

type InvoiceItem = {
    id: string;
    internal_id: string;
    issued_at: string;
    status: string;
    type: string;
    amount_currency: string;
    team_name: string;
    team_id: number;
    stripe_invoice_url: string | null;
};

type AffiliateStats = {
    totalClicks: number;
    totalSignups: number;
    totalPurchases: number;
    totalEarningsCzk: number;
    totalEarningsEur: number;
    totalEarningsUsd: number;
    totalPayoutsCzk: number;
    totalPayoutsEur: number;
    totalPayoutsUsd: number;
    toPayoutCzk: number;
    toPayoutEur: number;
    toPayoutUsd: number;
    affiliateLinks: AffiliateLinkItem[];
};

const props = defineProps<{
    user: any;
    integrations: any[];
    eventTypes: EventTypeItem[];
    reservations: Paginated<ReservationItem>;
    affiliateStats: AffiliateStats | null;
    linearTasks?: LinearTask[];
    invoices?: InvoiceItem[];
    billingAddresses: BillingAddressItem[];
    emails: EmailItem[];
}>();

const { t } = useI18n();
const { formatLastLogin, formatLastLoginFull } = useLastLoginFormat();

const activeTab = ref("event-types");
const linearTasks = computed(() => props.linearTasks ?? []);
const invoices = computed(() => props.invoices ?? []);
const billingAddresses = computed(() => props.billingAddresses ?? []);
const emails = computed(() => props.emails);

function formatName(ba: BillingAddressItem): string {
    const parts = [ba.first_name, ba.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "—";
}

function formatAddress(ba: BillingAddressItem): string {
    const line2 = [ba.postal_code, ba.city].filter(Boolean).join(" ");
    const parts = [ba.street, line2].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
}

type EmailThread = {
    subject: string;
    messages: EmailItem[];
    latestDate: string;
};

function normalizeSubject(subject: string): string {
    return subject
        .replace(/^(Re|Fwd|Fw|Odp|Přeposlat)\s*:\s*/gi, "")
        .trim()
        .toLowerCase();
}

const emailThreads = computed<EmailThread[]>(() => {
    const threadMap = new Map<string, EmailItem[]>();

    for (const email of emails.value) {
        const key = normalizeSubject(email.subject || "");
        if (!threadMap.has(key)) {
            threadMap.set(key, []);
        }
        threadMap.get(key)!.push(email);
    }

    const threads: EmailThread[] = [];
    for (const [, messages] of threadMap) {
        messages.sort(
            (a, b) =>
                new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
        );
        threads.push({
            subject: messages[0].subject || "No subject",
            messages,
            latestDate: messages[messages.length - 1].sent_at,
        });
    }

    threads.sort(
        (a, b) =>
            new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime(),
    );

    return threads;
});

const expandedThreads = ref<Set<number>>(new Set());

function toggleThread(index: number) {
    if (expandedThreads.value.has(index)) {
        expandedThreads.value.delete(index);
    } else {
        expandedThreads.value.add(index);
    }
}

const userInitials = computed(() => {
    const name = props.user.name ?? "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
});

// Email detail modal
const selectedEmail = ref<EmailItem | null>(null);

function openEmailDetail(email: EmailItem) {
    selectedEmail.value = email;
}

// Outreach email state
const showOutreachModal = ref(false);
const isGeneratingEmail = ref(false);
const isSendingEmail = ref(false);
const outreachAccounts = ref<{ email: string; display_name: string }[]>([]);
const outreachForm = ref({
    account_email: "",
    to_email: "",
    subject: "",
    body: "",
});

async function generateOutreachEmail() {
    isGeneratingEmail.value = true;
    try {
        const { data } = await axios.post(
            route("app.kiosk.users.outreach-email.show", {
                user: props.user.id,
            }),
        );
        outreachAccounts.value = data.accounts ?? [];
        outreachForm.value = {
            account_email: data.accounts?.[0]?.email ?? "",
            to_email: data.to_email,
            subject: data.subject,
            body: data.body,
        };
        showOutreachModal.value = true;
    } catch (e) {
        console.error("Failed to generate outreach email", e);
    } finally {
        isGeneratingEmail.value = false;
    }
}

async function sendOutreachEmail() {
    isSendingEmail.value = true;
    try {
        await axios.post(
            route("app.kiosk.users.outreach-email.store", {
                user: props.user.id,
            }),
            outreachForm.value,
        );
        showOutreachModal.value = false;
        window.location.reload();
    } catch (e) {
        console.error("Failed to send outreach email", e);
    } finally {
        isSendingEmail.value = false;
    }
}

// Delete confirmation state
const showFirstConfirmation = ref(false);
const showSecondConfirmation = ref(false);
const deleteForm = useForm({});

function openDeleteConfirmation() {
    showFirstConfirmation.value = true;
}

function proceedToSecondConfirmation() {
    showFirstConfirmation.value = false;
    showSecondConfirmation.value = true;
}

function cancelDelete() {
    showFirstConfirmation.value = false;
    showSecondConfirmation.value = false;
}

function confirmDelete() {
    deleteForm.delete(
        route("app.kiosk.users.destroy", { user: props.user.id }),
        {
            onSuccess: () => {
                showSecondConfirmation.value = false;
            },
        },
    );
}

function formatDate(iso: string): string {
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function reservationDateRange(r: ReservationItem): {
    start_date: string;
    end_date: string;
} {
    const dates = [new Date(r.created_at), new Date(r.updated_at)];
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    min.setDate(min.getDate() - 1);
    min.setHours(0, 0, 0, 0);
    max.setDate(max.getDate() + 1);
    max.setHours(23, 59, 0, 0);
    const fmt = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const h = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${y}-${m}-${day}T${h}:${mi}`;
    };
    return { start_date: fmt(min), end_date: fmt(max) };
}

function linearStateClass(stateType: string): string {
    const map: Record<string, string> = {
        completed: "bg-emerald-50 text-emerald-600",
        started: "bg-blue-50 text-blue-600",
        unstarted: "bg-iron-100 text-iron-500",
        backlog: "bg-iron-50 text-iron-400",
        canceled: "bg-red-50 text-red-600",
        cancelled: "bg-red-50 text-red-600",
    };
    return map[stateType.toLowerCase()] ?? "bg-iron-100 text-iron-500";
}

function invoiceStatusClass(status: string): string {
    const map: Record<string, string> = {
        paid: "bg-emerald-50 text-emerald-600",
        open: "bg-blue-50 text-blue-600",
        draft: "bg-iron-100 text-iron-500",
        void: "bg-iron-100 text-iron-400",
        uncollectible: "bg-red-50 text-red-600",
    };
    return map[status] ?? "bg-iron-100 text-iron-500";
}

function statusClass(status: string): string {
    const map: Record<string, string> = {
        synced: "bg-emerald-50 text-emerald-600",
        created: "bg-blue-50 text-blue-600",
        canceled: "bg-red-50 text-red-600",
        confirmed: "bg-emerald-50 text-emerald-600",
    };
    return map[status] ?? "bg-iron-100 text-iron-500";
}
</script>
